import { useQuery } from "@apollo/client";
import Form, { IChangeEvent } from "@rjsf/core";
import { Panel, Alert, theme } from "@xometry/xometry_loft";
import _ from "lodash";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  ReactElement,
  useMemo,
  Fragment,
} from "react";
import { v4 as uuidv4 } from "uuid";
import CheckmarkIcon from "./CheckmarkIcon.svg";
import {
  GetProposalTemplatesQuery,
  GetProposalTemplatesQueryResult,
  MarketplaceForm,
  MarketplaceProposalTemplateItem,
  ProposalTemplateFragment,
} from "../../schema/generated/schema";
import ProposalJsonForm from "../JsonSchemaLoftTheme";
import {
  CustomStyling,
  FormWrapper,
} from "../proposals/ProposalForm/ProposalForm.style";
import {
  Footer,
  FormContainer,
  FormStepperBox,
  Header,
  Step,
  StepCircle,
  StepLine,
  Stepper,
  StepperSpacer,
  StepTitle,
  TemplateTitle,
  TemplateX,
  RightButton,
  LeftButton,
  FormSection,
} from "./FormStepper.style";
import { scrollToErrors } from "../FormEditor/util";
import Logger from "../../shared/Logger";

interface FormStepperProps {
  templateSlug: string;
  onSubmit: (data: any) => Promise<boolean>;
  // eslint-disable-next-line react/require-default-props
  onClose?: () => void;
  // eslint-disable-next-line react/require-default-props
  onCancel?: () => void;
  successStep: {
    title: string;
    content: ReactElement;
  };
  // eslint-disable-next-line react/require-default-props
  isSubmittingButtonContent?: ReactElement;
  // eslint-disable-next-line react/require-default-props
  fileUploadStep?: {
    content: ReactElement;
    stepNumber: number;
    errorMessage?: string;
  };
  // eslint-disable-next-line react/require-default-props
  disableWhileSubmitting?: boolean;
  // eslint-disable-next-line react/require-default-props
  disableFileUploadNext?: boolean;
  // eslint-disable-next-line react/require-default-props
  disableFileUploadNextMessage?: string;
  // eslint-disable-next-line react/require-default-props
  customTitle?: string;
}

type JsonObject = { [key: string]: JsonValue };

export type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | JsonObject;

export type SubmissionData = {
  templateId: string;
  templateSubmissionId: string;
  forms: {
    formId: string;
    formData: {
      key: string;
      label: string;
      value: JsonValue;
    }[];
  }[];
};

const formDefaultsForTemplate = (
  form: MarketplaceForm
): Array<{ key: string; value: JsonValue; label: string }> => {
  return form.config.elements.map(e => {
    let emptyDefault: null | string | Array<string> = "";
    if (e.type === "MULTI_SELECT") {
      emptyDefault = [];
      // default value for select is null, this allows us to filter them out before the final submission
      // When we use the default values for a form submission, the element defaults form a template
      // for the values sent to the backend.
    } else if (e.type === "SELECT") {
      emptyDefault = null;
    }

    const isDefault =
      "defaultValue" in e &&
      typeof e.defaultValue !== "undefined" &&
      e.defaultValue !== null;
    const value = isDefault ? e.defaultValue : emptyDefault;
    return {
      key: e.key,
      label: e.label || "[Unknown Label]",
      value,
    };
  });
};

const isFormValid = (formData: any, formRef: Form<any>): boolean =>
  formRef.validate(formData).errors.length === 0;

const useTemplate = (templateSlug: string): ProposalTemplateFragment | null => {
  const templates = useQuery<GetProposalTemplatesQueryResult>(
    GetProposalTemplatesQuery,
    {
      variables: {
        partnerId: "",
      },
    }
  );

  const template = useMemo(() => {
    return (
      templates?.data?.getProposalTemplates.find(
        t => t.slug === templateSlug
      ) || null
    );
  }, [templates, templateSlug]);

  return template;
};

export type MarketplaceTemplateItemContent = {
  kind: "marketplace-form";
  formIndex: number;
  form: MarketplaceProposalTemplateItem;
};

export type JSXContent = {
  kind: "jsx-content";
  content: ReactElement;
};

export type StepContent = JSXContent | MarketplaceTemplateItemContent;

export const FormStepper: React.FC<FormStepperProps> = props => {
  const {
    onSubmit,
    onClose,
    onCancel,
    successStep,
    fileUploadStep,
    templateSlug,
    isSubmittingButtonContent,
    disableWhileSubmitting,
    disableFileUploadNext,
    disableFileUploadNextMessage,
    customTitle,
  } = props;
  const template = useTemplate(templateSlug);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formWasSubmitted, setFormWasSubmitted] = useState(false);
  const [formSubmitError, setFormSubmitError] = useState(false);
  const [UiIndex, setUiIndex] = useState(0);
  const [highestUiIndexReached, setHighestUiIndexReached] = useState(0);
  const [touchAllErrors, setTouchAllErrors] = useState<Array<boolean>>([]);
  const [formDatas, setFormDatas] = useState<
    Array<{ [key: string]: JsonValue }>
  >([]);
  const [displayFileUploadNextAlert, setDisplayFileUploadNextAlert] =
    useState(false);

  const formRefs = useRef<Array<Form<any>>>([]);

  // used only for validation logic and not for UI
  const numForms = template?.items.length || 0;

  const UiForms: StepContent[] = useMemo(() => {
    if (template) {
      const forms: StepContent[] = template.items.map((f, idx) => ({
        kind: "marketplace-form",
        formIndex: idx,
        form: f,
      }));
      if (!fileUploadStep) return forms;
      forms.splice(fileUploadStep.stepNumber - 1, 0, {
        kind: "jsx-content",
        content: fileUploadStep.content,
      });
      return forms;
    }

    return [];
  }, [template, fileUploadStep]);

  const validateCurrentStep = useCallback((): boolean => {
    const currentStep = UiForms[UiIndex];
    const formIndex =
      currentStep.kind === "marketplace-form"
        ? currentStep.formIndex
        : undefined;
    if (formIndex === undefined) return true;
    if (isFormValid(formDatas[formIndex], formRefs.current[formIndex])) {
      return true;
    }
    setTouchAllErrors(tae =>
      tae.map((obj, index) => (index === formIndex ? true : obj))
    );

    // Scroll to and focus on the first error
    setTimeout(() => {
      scrollToErrors(formRefs.current);
    }, 100);

    return false;
  }, [formDatas, UiIndex, UiForms]);

  useEffect(() => {
    setTouchAllErrors(new Array(numForms).fill(0).map(_ => false));
    setFormDatas(new Array(numForms).fill(0).map(_ => ({})));
  }, [numForms]);

  const stepTitles: string[] = (template?.items || [])
    .map(form => form.formVersion.formDataSchema.title)
    .concat([successStep.title]);

  if (fileUploadStep) {
    stepTitles.splice(fileUploadStep.stepNumber - 1, 0, "Upload Files");
  }
  return (
    (template && UiForms && (
      <FormStepperBox>
        <Header>
          <TemplateTitle>{customTitle || template.props.title}</TemplateTitle>
          {onClose && (
            <TemplateX
              onClick={() => {
                analytics.track("Form Modal Closed", {
                  context: "Form Stepper",
                  templateId: template.id,
                  templateName: template.props.title,
                });
                onClose();
              }}
            >
              <i className="far fa-times" />
            </TemplateX>
          )}
        </Header>
        <StepperSpacer />
        <Stepper>
          {stepTitles.map((title, index) => {
            return (
              <Fragment key={title}>
                <Step
                  isUnlocked={index <= highestUiIndexReached}
                  onClick={() => {
                    setUiIndex(index);
                  }}
                >
                  <StepCircle
                    isCompleted={index < UiIndex || formWasSubmitted}
                    isUnlocked={index <= highestUiIndexReached}
                    isActive={index === UiIndex}
                  >
                    <img src={CheckmarkIcon} alt="Step Complete" />
                  </StepCircle>
                  <StepTitle
                    isUnlocked={
                      index <= highestUiIndexReached || formWasSubmitted
                    }
                  >
                    {title}
                  </StepTitle>
                </Step>
                {index !== stepTitles.length - 1 && (
                  <StepLine
                    isUnlocked={
                      index + 1 <= highestUiIndexReached || formWasSubmitted
                    }
                  />
                )}
              </Fragment>
            );
          })}
        </Stepper>
        <FormSection>
          {UiForms.map((templateItem, index) => {
            if (templateItem.kind === "marketplace-form") {
              return (
                <FormContainer
                  key={templateItem.form.id}
                  isSelected={!formWasSubmitted && index === UiIndex}
                >
                  <Panel css={CustomStyling} kind="primary">
                    <div css={FormWrapper}>
                      <ProposalJsonForm
                        id={templateItem.form.formVersion.versionId}
                        ref={ref => {
                          if (ref) {
                            formRefs.current = [...formRefs.current, ref];
                          }
                        }}
                        schema={_.omit(
                          templateItem.form.formVersion.formDataSchema,
                          ["title", "description"]
                        )}
                        elements={templateItem.form.formVersion.config.elements}
                        elementArrangement={
                          templateItem.form.formVersion.config
                            .elementArrangement
                        }
                        onChange={(event: IChangeEvent<any>) => {
                          setFormDatas(fd =>
                            fd.map((obj, i) =>
                              i === templateItem.formIndex
                                ? event.formData
                                : obj
                            )
                          );
                        }}
                        isSeller={false}
                        canEdit
                        touchAllErrors={touchAllErrors[templateItem.formIndex]}
                      />
                    </div>
                  </Panel>
                </FormContainer>
              );
            }
            return (
              <FormContainer
                key="upload-file-step"
                isSelected={!formWasSubmitted && index === UiIndex}
              >
                {templateItem.content}
              </FormContainer>
            );
          })}
          {successStep && (
            <FormContainer key="success-page" isSelected={formWasSubmitted}>
              {successStep.content}
            </FormContainer>
          )}
        </FormSection>
        {!formWasSubmitted && (
          <Footer>
            {UiIndex !== 0 && (
              <LeftButton
                kind="text-blue"
                scale="large"
                onClick={() => {
                  setUiIndex(UiIndex - 1);
                }}
              >
                Go Back
              </LeftButton>
            )}
            {onCancel !== undefined && UiIndex === 0 && (
              <LeftButton kind="text-blue" scale="large" onClick={onCancel}>
                Cancel
              </LeftButton>
            )}
            {UiIndex + 1 < UiForms.length && (
              <>
                {!!disableFileUploadNext &&
                  fileUploadStep &&
                  displayFileUploadNextAlert &&
                  !!disableFileUploadNextMessage && (
                    <Alert kind={theme.kind.danger}>
                      {disableFileUploadNextMessage}
                    </Alert>
                  )}
                <RightButton
                  kind="primary"
                  scale="large"
                  onClick={() => {
                    // if on fileUpload stage: ensure files are uploaded (and toggle
                    // alert if they are not) before progressing
                    if (fileUploadStep) {
                      setDisplayFileUploadNextAlert(!!disableFileUploadNext);
                      if (disableFileUploadNext) {
                        return;
                      }
                    }
                    // successfully progressing to future slides of menu
                    const currentForm = UiForms[UiIndex];
                    if (
                      currentForm.kind === "marketplace-form" &&
                      !validateCurrentStep()
                    ) {
                      analytics.track("Form Step Failed", {
                        context: "Form Stepper",
                        templateId: template.id,
                        templateName: template.props.title,
                        formId: currentForm.form.formId,
                        formName: stepTitles[UiIndex],
                        formSlug: templateSlug,
                      });
                      return;
                    }
                    if (UiIndex + 1 > highestUiIndexReached) {
                      setHighestUiIndexReached(UiIndex + 1);
                    }
                    // we need this because only the forms of kind "marketplace-form" have ids
                    switch (currentForm.kind) {
                      case "marketplace-form":
                        analytics.track("Form Step Completed", {
                          context: "Form Stepper",
                          templateId: template.id,
                          templateName: template.props.title,
                          formId: currentForm.form.formId,
                          formName: stepTitles[UiIndex],
                          formSlug: templateSlug,
                        });
                        break;
                      default:
                        analytics.track("Form Step Completed", {
                          context: "Form Stepper",
                          templateId: template.id,
                          templateName: template.props.title,
                          formId: "Hardcoded form",
                          formName: stepTitles[UiIndex],
                          formSlug: templateSlug,
                        });
                    }
                    setUiIndex(UiIndex + 1);
                  }}
                >
                  Next
                </RightButton>
              </>
            )}
            {UiIndex + 1 === UiForms.length && (
              <>
                {formSubmitError && (
                  <Alert kind={theme.kind.danger}>
                    Due to an error we were unable to submit the form
                  </Alert>
                )}
                <RightButton
                  kind="primary"
                  scale="large"
                  disabled={disableWhileSubmitting && isSubmitting}
                  onClick={async () => {
                    if (isSubmitting) {
                      return;
                    }
                    setFormSubmitError(false);

                    if (!validateCurrentStep()) {
                      return;
                    }

                    const submissionData: SubmissionData = {
                      templateId: template?.id,
                      templateSubmissionId: uuidv4(),
                      forms: (template?.items || []).map((f, idx) => {
                        const formDefaults = formDefaultsForTemplate(
                          f.formVersion
                        );
                        const formData = formDefaults.map(formField => {
                          if (
                            formField.key in formDatas[idx] &&
                            typeof formDatas[idx][formField.key] !== "undefined"
                          ) {
                            return {
                              ...formField,
                              value: formDatas[idx][formField.key],
                            };
                          }
                          return formField;
                        });
                        return {
                          formId: f.formId,
                          formData: formData.filter(e => e.value !== null),
                        };
                      }),
                    };

                    try {
                      setIsSubmitting(true);
                      const result = await onSubmit(submissionData);
                      if (result) {
                        setFormWasSubmitted(true);
                        analytics.track("Form Submitted", {
                          context: "Form Stepper",
                          templateName: template.props.title,
                          templateId: template.id,
                        });
                      } else {
                        setFormSubmitError(true);
                        Logger.error(
                          "Failed to submit form: No result returned",
                          undefined,
                          {
                            context: "Form Stepper",
                            templateName: template.props.title,
                            templateId: template.id,
                          }
                        );
                      }
                    } catch (e) {
                      Logger.error("Failed to submit form: Unknown error", e, {
                        context: "Form Stepper",
                        templateName: template.props.title,
                        templateId: template.id,
                      });
                      setFormSubmitError(true);
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                >
                  {isSubmitting && isSubmittingButtonContent !== undefined
                    ? isSubmittingButtonContent
                    : template.props.submitButtonText || "Submit"}
                </RightButton>
              </>
            )}
          </Footer>
        )}
      </FormStepperBox>
    )) || <></>
  );
};

export default FormStepper;
